import * as Sentry from '@sentry/react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
} from 'react-router';

import { NAVIGATION } from '@src/constants';
import { AuthLayout } from '@src/layouts/AuthLayout/AuthLayout';
import { DashboardLayout } from '@src/layouts/DashboardLayout/DashboardLayout';
import {
  AppLoginPage,
  CancelAfterFreeMonthPage,
  CancelSubscriptionPage,
  CancelSubscriptionReminderPage,
  ContactPage,
  CreatePasswordPage,
  DownloadAppPage,
  HomePage,
  LoginPage,
  ManageSubscriptionPage,
  NewPasswordPage,
  OfferSubscriptionPage,
  Page404,
  PauseSubscriptionPage,
  PaymentPage,
  PolicyPage,
  ProfileFastingPlansPage,
  ProfileLoginInApp,
  ProfileOneFreeMonthPage,
  ProfilePage,
  QuizPage,
  ResetPasswordPage,
  TermsPage,
} from '@src/pages';

import { ProtectedRoute } from './ProtectedRoute';
import { RootErrorBoundary } from './RootErrorBoundary';
import { RootRouter } from './RootRouter';

const routerRoutes = createRoutesFromElements(
  <Route element={<RootRouter />} errorElement={<RootErrorBoundary />}>
    <Route path={NAVIGATION.HOME}>
      <Route index element={<HomePage />} />
      <Route path={NAVIGATION.SURVEY} element={<HomePage />} />
      <Route path={NAVIGATION.QUIZ} element={<QuizPage />} />
      <Route path={NAVIGATION.PAYMENT} element={<PaymentPage />} />
      <Route path={NAVIGATION.DOWNLOAD_APP} element={<DownloadAppPage />} />
      <Route path={NAVIGATION.APP_LOGIN} element={<AppLoginPage />} />
      <Route path={NAVIGATION.POLICY} element={<PolicyPage />} />
      <Route path={NAVIGATION.TERMS} element={<TermsPage />} />
      <Route path={NAVIGATION.CONTACT} element={<ContactPage />} />
      <Route
        path={NAVIGATION.CREATE_PASSWORD}
        element={<CreatePasswordPage />}
      />
      <Route path={NAVIGATION.NEW_PASSWORD} element={<NewPasswordPage />} />

      {/* Old webflow links support */}
      <Route path={NAVIGATION.WEBFLOW_HOME} element={<HomePage />} />
      <Route path={NAVIGATION.WEBFLOW_PAYMENT} element={<PaymentPage />} />
      <Route
        path={NAVIGATION.WEBFLOW_PAYMENT_EXPIRED}
        element={<PaymentPage />}
      />
      <Route
        path={NAVIGATION.WEBFLOW_NEW_PASSWORD}
        element={<NewPasswordPage />}
      />
      <Route path={NAVIGATION.WEBFLOW_QUIZ} element={<QuizPage />} />
      <Route
        path={NAVIGATION.WEBFLOW_CREATE_PASSWORD}
        element={<CreatePasswordPage />}
      />
      <Route
        path={NAVIGATION.WEBFLOW_DOWNLOAD_APP}
        element={<DownloadAppPage />}
      />
      <Route
        path={NAVIGATION.WEBFLOW_CANCEL_SUBSCRIPTION}
        element={<CancelSubscriptionPage />}
      />
      <Route
        path={NAVIGATION.WEBFLOW_RESET_PASSWORD}
        element={<ResetPasswordPage />}
      />
      <Route path={NAVIGATION.WEBFLOW_PROFILE} element={<ProfilePage />} />
      <Route
        path={NAVIGATION.WEBFLOW_MANAGE_SUBSCRIPTION}
        element={<ManageSubscriptionPage />}
      />
    </Route>
    <Route
      path={NAVIGATION.AUTH}
      element={
        <ProtectedRoute type="guest" redirectPath={NAVIGATION.PROFILE}>
          <AuthLayout />
        </ProtectedRoute>
      }
    >
      <Route path={NAVIGATION.LOGIN} element={<LoginPage />} />
      <Route path={NAVIGATION.RESET_PASSWORD} element={<ResetPasswordPage />} />
    </Route>
    <Route
      path={NAVIGATION.PROFILE}
      element={
        <ProtectedRoute type="authorized" redirectPath={NAVIGATION.LOGIN}>
          <DashboardLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<ProfilePage />} />
      <Route
        path={NAVIGATION.MANAGE_SUBSCRIPTION}
        element={<ManageSubscriptionPage />}
      />
    </Route>
    <Route
      path={NAVIGATION.PROFILE}
      element={
        <ProtectedRoute type="authorized" redirectPath={NAVIGATION.LOGIN}>
          <Outlet />
        </ProtectedRoute>
      }
    >
      {/* Cancellation Subscription Pages */}
      <Route
        path={NAVIGATION.CANCEL_SUBSCRIPTION}
        element={<CancelSubscriptionPage />}
      />
      <Route
        path={NAVIGATION.CANCEL_REMINDER_SUBSCRIPTION}
        element={<CancelSubscriptionReminderPage />}
      />
      <Route
        path={NAVIGATION.OFFER_SUBSCRIPTION}
        element={<OfferSubscriptionPage />}
      />
      <Route
        path={NAVIGATION.PAUSE_SUBSCRIPTION}
        element={<PauseSubscriptionPage />}
      />
      <Route
        path={NAVIGATION.PROFILE_FASTING_PLANS}
        element={<ProfileFastingPlansPage />}
      />
      <Route
        path={NAVIGATION.PROFILE_ONE_FREE_MONTH}
        element={<ProfileOneFreeMonthPage />}
      />
      <Route
        path={NAVIGATION.PROFILE_LOGIN_IN_APP}
        element={<ProfileLoginInApp />}
      />
      <Route
        path={NAVIGATION.CANCEL_AFTER_FREE_MONTH_SUBSCRIPTION}
        element={<CancelAfterFreeMonthPage />}
      />
    </Route>

    <Route path="*" element={<Page404 />} />

    {/* For testing error boundary component */}
    <Route
      path="/__error-test"
      loader={() => {
        throw new Response('Not Found', {
          status: 404,
          statusText: 'Not Found',
        });
      }}
      element={<div />}
    />
  </Route>,
);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(routerRoutes);
